import React, { useState } from 'react'
import classnames from 'classnames'
import formattedCreditsWithHoursShort from '@Utils/FormattedCreditsWithHoursShort'
import Plus from '@Images/projects_table_new_project_plus.svg'
import Tooltip from '@SiteComponents/Tooltip'
import useTooltipDelay from '@Utils/UseTooltipDelay'
import Button from '@SiteComponents/Button'
import FiltersDropdown from '@SiteComponents/FiltersDropdown'
import LanguagesManager from '@SiteComponents/LanguagesManager'
import Search from '@Images/search_voice_studio.svg'
import CloseSearch from '@Images/projects_table_header_search_close.svg'
import Languages from '@Images/filters_dropdown_languages.svg'

import {
  TSpeakerVoiceUsedLanguages,
} from '@SiteModules/SpeakerVoice/types'

import './SpeakerVoicesTableHeader.scss'

interface SpeakerVoicesTableHeaderProps {
  readonly currentWorkspaceId: number
  readonly totalCountOfSpeakerVoices: number
  readonly availableTotalCredits: number
  readonly speakerVoicesLoading: boolean
  readonly usedLanguages: TSpeakerVoiceUsedLanguages
  readonly languagesNames: string[]
  readonly selectedLanguagesNames: string[]
  readonly maxSpeakerVoicesLimit?: number
  createNewSpeakerVoiceAndOpenIt(): void
  handleSearch(searchTerm?: string): void
  changeSelectedLanguageNames(e: React.ChangeEvent<HTMLInputElement>, languageName: string): void
  resetSelectedLanguagesNames(): void
}

const SpeakerVoicesTableHeader: React.FC<SpeakerVoicesTableHeaderProps> = props => {
  const {
    currentWorkspaceId,
    totalCountOfSpeakerVoices,
    availableTotalCredits,
    usedLanguages,
    languagesNames,
    selectedLanguagesNames,
    maxSpeakerVoicesLimit,
  } = props

  const hasValidCurrentWorkspace = currentWorkspaceId >= 0
  const hasReachedMaxSpeakerVoicesLimit = maxSpeakerVoicesLimit !== undefined && totalCountOfSpeakerVoices >= maxSpeakerVoicesLimit
  const shouldDisableNewVoiceButton = !hasValidCurrentWorkspace || hasReachedMaxSpeakerVoicesLimit

  const {
    showTooltip,
    triggerShowTooltip,
    cancelTooltip,
  } = useTooltipDelay()

  const [searchText, setSearchText] = useState('')
  const [submittedSearchText, setSubmittedSearchText] = useState<string | undefined>(undefined)
  const [isFocused, setIsFocused] = useState(false)

  const onFocus = () => {
    setIsFocused(true)
  }

  const onBlur = () => {
    setIsFocused(false)
  }

  const handleSearch = (value?: string, force?: boolean) => {
    if (submittedSearchText === value && !force) { return }

    setSubmittedSearchText(value)
    props.handleSearch(value)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(searchText)
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }

  const resetSearchText = () => {
    setSearchText('')
    handleSearch('')
  }

  const onNewVoiceButtonMouseEnter = () => {
    if (!hasReachedMaxSpeakerVoicesLimit) { return }

    triggerShowTooltip()
  }

  const onNewVoiceButtonMouseLeave = () => {
    cancelTooltip()
  }

  const renderSpeakerVoicesTableHeaderTop = () => {
    return (
      <div className="SpeakerVoicesTableHeader__top">
        {renderSpeakerVoicesTableHeaderTopLeft()}
        {renderSpeakerVoicesTableHeaderTopRight()}
      </div>
    )
  }

  const renderSpeakerVoicesTableHeaderTopLeft = () => {
    return (
      <div className="SpeakerVoicesTableHeader__top-left">
        <div className="SpeakerVoicesTableHeader__name">
          Voice Studio
        </div>
        <div className="SpeakerVoicesTableHeader__number-of-speaker-voices">
          {totalCountOfSpeakerVoices === 1 ? '1 voice' : `${totalCountOfSpeakerVoices} voices`}
        </div>
      </div>
    )
  }

  const renderSpeakerVoicesTableHeaderTopRight = () => {
    return (
      <div className="SpeakerVoicesTableHeader__top-right">
        <div className="SpeakerVoicesTableHeader__credits-remaining">
          {`${formattedCreditsWithHoursShort(availableTotalCredits)} credits remaining`}
        </div>
      </div>
    )
  }

  const renderSpeakerVoicesTableHeaderBottom = () => {
    return (
      <div className="SpeakerVoicesTableHeader__bottom">
        <div className="SpeakerVoicesTableHeader__bottom-left">
          {renderSearchInput()}
          {renderFilters()}
        </div>
        <div className="SpeakerVoicesTableHeader__bottom-right">
          {renderNewVoiceButton()}
        </div>
      </div>
    )
  }

  const renderSearchInput = () => {
    return (
      <div className={classnames(
        'SpeakerVoicesTableHeader__search',
        { 'SpeakerVoicesTableHeader__search-focused': isFocused }
      )}>
        <Search className="SpeakerVoicesTableHeader__search-icon"/>
        <CloseSearch
          className={classnames(
            'SpeakerVoicesTableHeader__search-icon-close',
            { 'SpeakerVoicesTableHeader__search-icon-close-hidden': !isFocused }
          )}
          onClick={resetSearchText}
        />
        <div className="SpeakerVoicesTableHeader__search-input-wrapper">
          <input
            placeholder="Search..."
            autoComplete="off"
            value={searchText}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        </div>
      </div>
    )
  }

  const renderFilters = () => {
    const hasNoUsedLanguages = Object.keys(usedLanguages).length === 0

    return (
      <FiltersDropdown
        categories={[{
          name: 'Languages',
          icon: <Languages />,
          selectedItemIds: selectedLanguagesNames,
          selectedItemCaptions: selectedLanguagesNames,
          itemManagerFactory: renderLanguagesManager,
          resetItemSelection: props.resetSelectedLanguagesNames,
        }]}
        disabled={hasNoUsedLanguages}
      />
    )
  }

  const renderLanguagesManager = () => {
    return (
      <LanguagesManager
        usedLanguages={usedLanguages}
        languagesNames={languagesNames}
        selectedLanguagesNames={selectedLanguagesNames}
        changeSelectedLanguageNames={props.changeSelectedLanguageNames}
      />
    )
  }

  const renderNewVoiceButton = () => {
    return (
      <div
        className="SpeakerVoicesTableHeader__new-voice-button"
        onMouseEnter={onNewVoiceButtonMouseEnter}
        onMouseLeave={onNewVoiceButtonMouseLeave}
      >
        <Button
          caption="New voice"
          icon={<Plus />}
          onClick={props.createNewSpeakerVoiceAndOpenIt}
          styleType="brand"
          size="large"
          iconRight
          disabled={shouldDisableNewVoiceButton}
        />
        <div className="SpeakerVoicesTableHeader__tooltip">
          {showTooltip && (
            <Tooltip
              text={`You have reached the maximum number of voices (${maxSpeakerVoicesLimit}).`}
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="SpeakerVoicesTableHeader">
      {renderSpeakerVoicesTableHeaderTop()}
      {renderSpeakerVoicesTableHeaderBottom()}
    </div>
  )
}

export default SpeakerVoicesTableHeader
